import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { api } from '@/lib/api';
import { getApiErrorMessage } from '@/lib/api-error';
import type { VeiculoRow } from '@/lib/types';
import { formatDocumentoExibicao } from '@/lib/format';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePlacaSearch } from '@/contexts/PlacaSearchContext';

export function PlacaSearchModal() {
  const { open, setOpen } = usePlacaSearch();
  const [placa, setPlaca] = useState('');
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: ['veiculo-placa', placa],
    queryFn: () =>
      api.get<VeiculoRow | null>('/veiculos', { params: { placa: placa.trim() } }).then((r) => r.data),
    enabled: open && placa.trim().length >= 5,
  });

  const v = q.data;

  const irOs = () => {
    if (!v) return;
    setOpen(false);
    navigate(`/os?clienteId=${v.pessoa.id}&veiculoId=${v.id}`);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">Buscar por placa</DialogTitle>
          <DialogDescription>
            Atalho <span className="font-os rounded border border-border bg-muted px-1.5 py-0.5">F2</span> em qualquer
            tela. Digite a placa e confira o vínculo cliente/veículo.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="placa-modal">Placa</Label>
            <Input
              id="placa-modal"
              className="font-os text-base uppercase"
              placeholder="ABC1D23"
              value={placa}
              onChange={(e) => setPlaca(e.target.value.toUpperCase())}
              autoFocus
            />
          </div>
          {q.isFetching && <p className="text-sm text-muted-foreground">A pesquisar…</p>}
          {q.error && <p className="text-sm text-destructive">{getApiErrorMessage(q.error)}</p>}
          {v && (
            <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
              <p className="font-medium">
                <span className="font-os">{v.placa}</span> — {v.modelo ?? '—'}
              </p>
              <p className="text-muted-foreground">
                Cliente: {v.pessoa.nomeRazao} · CPF/CNPJ {formatDocumentoExibicao(v.pessoa.documento)}
              </p>
            </div>
          )}
          {open && placa.trim().length >= 5 && !q.isFetching && !q.error && !v && (
            <p className="text-sm text-muted-foreground">Nenhum veículo ativo com esta placa.</p>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
            Fechar
          </Button>
          <Button type="button" variant="default" disabled={!v} onClick={irOs}>
            <Search className="mr-2 h-4 w-4" />
            Nova OS com este veículo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
